import React, {useState, useEffect} from "react";
import * as chartjs from "chart.js";
import { ChartOptions } from "chart.js";
import { Line } from "react-chartjs-2";
import { Coordinate2D } from "../../Model/Coordinate";

interface FocusData extends chartjs.ChartData {};

type Props = {
  focusScoreCoordinate: Array<Coordinate2D>;
  onClickFocus: (index: number) => void;
  selectedIndex: number
  style?: React.CSSProperties;
};

const FocusLineChart = ({
  focusScoreCoordinate,
  style = {},
  onClickFocus = () => {},
  selectedIndex
}: Props) => {
  const [selectedFocusIndex, setSelectedDataIndex] = useState(selectedIndex);
  const [minFocus, setMinFocus] = useState(0);
  const [maxFocus, setMaxFocus] = useState(1);

  useEffect(() => {
    setSelectedDataIndex(selectedIndex);
  }, [selectedIndex])

  useEffect(() => {
    const minimumFocus = Math.min(
      ...focusScoreCoordinate.map((focus) => focus.x)
    );
    const maximumFocus = Math.max(
      ...focusScoreCoordinate.map((focus) => focus.x)
    );
    setMinFocus(minimumFocus);
    setMaxFocus(maximumFocus);
  }, [focusScoreCoordinate]);

  const onClickChart = (evt: MouseEvent, item: Array<any>) => {
    if (item.length > 0) {
      setSelectedDataIndex(item[0]._index);
      onClickFocus(item[0]._index);
    }
  };

  const chartOptions: ChartOptions = {
    legend: {
      display: false,
    },
    maintainAspectRatio: false,
    scales: {
      xAxes: [
        {
          id: "absolute-scale",
          type: "linear",
          position: "bottom",
          scaleLabel: {
            labelString: "Focus",
            display: true,
          },
          ticks: {
            min: minFocus,
            max: maxFocus,
            autoSkip: true,
            autoSkipPadding: 50,
          },
        },
        {
          id: "percentage-scale",
          type: "linear",
          position: "top",
          ticks: {
            min: minFocus,
            max: maxFocus,
            autoSkip: true,
            autoSkipPadding: 50,
            callback: function (value: number) {
              return ((value / maxFocus) * 100).toFixed(1) + "%";
            },
          },
        },
      ],
      yAxes: [
        {
          type: "linear",
          position: "left",
          scaleLabel: {
            labelString: "Z Position",
            display: true,
          },
        },
      ],
    },
    plugins: {
      datalabels: {
        display: false,
      },
    },
    tooltips: {
      enabled: false,
    },
    animation: {
      duration: 0,
    },
    onClick: onClickChart,
    layout: {
      padding: {
        right: 20
      }
    }
  };

  const getDataSet = (
    data: Array<Coordinate2D>,
    selectedIndex: number
  ) => {
    const pointsColor: string[] = [];
    const pointsRadius: number[] = [];

    for (let i = 0; i < data.length; i++) {
      if (i === selectedIndex) {
        pointsColor.push("#F7A246");
        pointsRadius.push(8);
      } else {
        pointsColor.push("#27AE60");
        pointsRadius.push(5);
      }
    }

    const dataSet: chartjs.ChartDataSets = {
      xAxisID: "absolute-scale",
      label: "Line Dataset",
      fill: false,
      borderColor: "#27AE60",
      lineTension: 0,
      radius: pointsRadius,
      pointRadius: pointsRadius,
      pointBorderColor: pointsColor,
      pointHoverRadius: pointsRadius,
      pointBackgroundColor: pointsColor,
      pointHoverBackgroundColor: pointsColor,
      pointHoverBorderColor: pointsColor,
      hitRadius: 10,
      data: data,
    };
    return dataSet;
  };

  const focusData: FocusData = {
    datasets: [getDataSet(focusScoreCoordinate, selectedFocusIndex)],
  };

  return (
    <div
      style={{
        ...style,
      }}
    >
      {focusScoreCoordinate.length > 0 && <Line options={chartOptions} data={focusData} />}
    </div>
  );
};

export default FocusLineChart;